import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Users, Crown, User, Trash2 } from 'lucide-react';

const UsersTab = ({ users, currentUser, onUpdateRole, onDeleteUser }) => {
  return (
    <Card className="rounded-2xl border-[#E0D6C8]">
      <CardHeader>
        <CardTitle className="text-xl font-['Manrope'] flex items-center gap-2">
          <Users className="w-5 h-5 text-[#5C4023]" />
          User Management
        </CardTitle>
        <CardDescription>
          Manage staff accounts and admin permissions
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px]">
          <div className="space-y-3">
            {users.map((u) => (
              <div 
                key={u.id}
                className="flex items-center justify-between p-4 bg-[#F3EFE9]/50 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    u.role === 'admin' ? 'bg-amber-100' : 'bg-[#5C4023]/10'
                  }`}>
                    {u.role === 'admin' ? (
                      <Crown className="w-5 h-5 text-amber-600" />
                    ) : (
                      <User className="w-5 h-5 text-[#5C4023]" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{u.name}</p>
                    <p className="text-sm text-[#5A5A5A]">{u.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Select
                    value={u.role || 'staff'}
                    onValueChange={(v) => onUpdateRole(u.id, v)}
                    disabled={u.email === currentUser?.email}
                  >
                    <SelectTrigger 
                      data-testid={`role-select-${u.id}`}
                      className="w-28 rounded-lg"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-2" />
                          Staff
                        </div>
                      </SelectItem>
                      <SelectItem value="admin">
                        <div className="flex items-center">
                          <Crown className="w-4 h-4 mr-2" />
                          Admin
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {u.email === currentUser?.email ? (
                    <Badge variant="outline" className="text-xs">You</Badge>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-user-${u.id}`}
                          className="h-8 w-8 text-[#E07A5F] hover:text-white hover:bg-[#E07A5F]"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-2xl">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete User?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete <strong>{u.name}</strong> ({u.email}). This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => onDeleteUser(u.id, u.name)}
                            className="bg-[#E07A5F] hover:bg-[#D06045] rounded-full"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default UsersTab;
